import {
  normaliseName,
  type SaveWineRatingRequest,
  type Suggestions,
} from "@corklog/shared";
import { useTranslation } from "react-i18next";

import { control, fieldLabel, resting } from "./controls.js";
import { Picker } from "./Picker.js";

/**
 * Ratings typed while a wine is being registered, rather than afterwards.
 *
 * The wine's own page is where a reading is recorded, corrected and removed —
 * see `WineRatings` — and that is still true. What this adds is the ordinary
 * case: a bottle is catalogued with the Vivino average and the Munskänkarna
 * score already in front of you, and asking for them on a second screen is
 * asking for them to be entered later, which in practice means never.
 *
 * **These are drafts and nothing else.** They are not written when they are
 * typed, because the wine they belong to does not exist yet — it has no id
 * until the registration succeeds. The form collects them, the screen writes
 * them after the wine is created, and a rating that fails leaves a registered
 * wine with its ratings section open at the page it lands on. That is worse
 * than a transaction and better than the alternative, which would be putting
 * ratings into the wine's own contract and then having to decide what an *edit*
 * of the wine means for readings taken on dates.
 *
 * A row with no source or no score is not a rating and is dropped in silence.
 * An empty row is what an unused row looks like, not a mistake to be reported.
 */
export type RatingDraft = {
  sourceName: string;
  score: string;
  readOn: string;
  /** Only asked for, and only used, when the source is new to the cellar. */
  scaleMax: string;
};

export function emptyRatingDraft(): RatingDraft {
  return {
    sourceName: "",
    score: "",
    readOn: new Date().toISOString().slice(0, 10),
    scaleMax: "100",
  };
}

/**
 * The drafts worth writing, as requests.
 *
 * The scale is sent only for a source the cellar does not have: an existing one
 * already knows what its numbers are out of, and restating it would let one
 * reading change what every earlier reading from that source meant.
 */
export function ratingRequests(
  drafts: readonly RatingDraft[],
  suggestions: Suggestions | null,
): SaveWineRatingRequest[] {
  const sources = suggestions?.ratingSources ?? [];
  const requests: SaveWineRatingRequest[] = [];
  for (const draft of drafts) {
    const name = draft.sourceName.trim();
    const score = Number(draft.score.replace(",", "."));
    if (name === "" || draft.score.trim() === "" || !Number.isFinite(score)) continue;
    const known = sources.find(
      (source) => normaliseName(source.name) === normaliseName(name),
    );
    requests.push({
      sourceName: name,
      scaleMax: known === undefined ? Number(draft.scaleMax) : null,
      scaleHasDecimals: known?.scaleHasDecimals ?? true,
      score,
      readOn: draft.readOn,
      ratingCount: null,
      url: null,
      note: null,
    });
  }
  return requests;
}

export function RatingDrafts({
  drafts,
  onChange,
  suggestions,
}: {
  drafts: readonly RatingDraft[];
  onChange: (drafts: RatingDraft[]) => void;
  suggestions: Suggestions | null;
}) {
  const { t } = useTranslation();
  const sources = suggestions?.ratingSources ?? [];

  const update = (index: number, change: Partial<RatingDraft>) => {
    onChange(drafts.map((draft, at) => (at === index ? { ...draft, ...change } : draft)));
  };

  return (
    <div className="col-span-full">
      {drafts.map((draft, index) => {
        const known = sources.find(
          (source) => normaliseName(source.name) === normaliseName(draft.sourceName),
        );
        return (
          <div
            key={index}
            className="mt-4 grid gap-4 first:mt-0 sm:grid-cols-[2fr_1fr_1fr_1fr]"
          >
            <Picker
              id={`draft-source-${index}`}
              label={t("wine.ratings.source")}
              value={draft.sourceName}
              onChange={(value) => update(index, { sourceName: value })}
              options={sources.map((source) => source.name)}
              placeholder={t("wine.ratings.sourceHint")}
            />

            <div>
              <label className={fieldLabel} htmlFor={`draft-score-${index}`}>
                {known === undefined
                  ? t("wine.ratings.score")
                  : t("wine.ratings.scoreOf", { max: known.scaleMax })}
              </label>
              <input
                id={`draft-score-${index}`}
                inputMode="decimal"
                value={draft.score}
                onChange={(event) => update(index, { score: event.target.value })}
                className={`${control} ${resting} mt-1`}
              />
            </div>

            {/* Munskänkarna's twenty, the first time it is used. After that the
                cellar knows, and the field goes away for good. */}
            {known === undefined ? (
              <div>
                <label className={fieldLabel} htmlFor={`draft-scale-${index}`}>
                  {t("wine.ratings.scale")}
                </label>
                <input
                  id={`draft-scale-${index}`}
                  inputMode="numeric"
                  value={draft.scaleMax}
                  onChange={(event) => update(index, { scaleMax: event.target.value })}
                  className={`${control} ${resting} mt-1`}
                />
              </div>
            ) : (
              <div aria-hidden="true" />
            )}

            <div>
              <label className={fieldLabel} htmlFor={`draft-date-${index}`}>
                {t("wine.ratings.readOn")}
              </label>
              <input
                id={`draft-date-${index}`}
                type="date"
                value={draft.readOn}
                onChange={(event) => update(index, { readOn: event.target.value })}
                className={`${control} ${resting} mt-1`}
              />
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="text-accent mt-3 text-sm underline-offset-4 hover:underline"
        onClick={() => onChange([...drafts, emptyRatingDraft()])}
      >
        {t("wine.ratings.addAnother")}
      </button>
    </div>
  );
}
